import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Linkedin, Instagram, Globe, Loader2, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORMS = {
  linkedin_company: { label: 'LinkedIn Aziendale', color: 'bg-blue-600 text-white', icon: Linkedin },
  linkedin_personal: { label: 'LinkedIn Personale', color: 'bg-indigo-600 text-white', icon: Linkedin },
  instagram: { label: 'Instagram', color: 'bg-gradient-to-br from-purple-500 to-pink-500 text-white', icon: Instagram },
  facebook: { label: 'Facebook', color: 'bg-blue-500 text-white', icon: Globe },
  twitter: { label: 'X / Twitter', color: 'bg-slate-900 text-white', icon: Globe },
};

const EMPTY_FORM = { name: '', platform: 'linkedin_company', owner: '', active: true, notes: '', style_guide: '', buffer_profile_id: '', priority: 1 };

export default function SocialProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const [bufferChannels, setBufferChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadProfiles = useCallback(async () => {
    try {
      const res = await api.get('/social-profiles');
      setProfiles(res.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  const loadBufferChannels = useCallback(async () => {
    try {
      const res = await api.get('/buffer/profiles');
      setBufferChannels(res.data.profiles || []);
    } catch { setBufferChannels([]); }
  }, []);

  useEffect(() => { loadProfiles(); loadBufferChannels(); }, [loadProfiles, loadBufferChannels]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Inserisci il nome del profilo');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/social-profiles/${editing}`, form);
        toast.success('Profilo aggiornato');
      } else {
        await api.post('/social-profiles', form);
        toast.success('Profilo creato');
      }
      setOpen(false);
      setForm(EMPTY_FORM);
      setEditing(null);
      loadProfiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally { setSaving(false); }
  };

  const handleEdit = (p) => {
    setForm({
      name: p.name, platform: p.platform, owner: p.owner || '',
      active: p.active, notes: p.notes || '', style_guide: p.style_guide || '',
      buffer_profile_id: p.buffer_profile_id || '', priority: p.priority || 1,
    });
    setEditing(p.profile_id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo profilo?')) return;
    try {
      await api.delete(`/social-profiles/${id}`);
      toast.success('Profilo eliminato');
      loadProfiles();
    } catch { toast.error('Errore eliminazione'); }
  };

  const handleToggle = async (p) => {
    try {
      await api.put(`/social-profiles/${p.profile_id}`, { active: !p.active });
      loadProfiles();
    } catch { toast.error('Errore'); }
  };

  // Map buffer IDs to channel info for display
  const bufferMap = {};
  bufferChannels.forEach(ch => { bufferMap[ch.id] = ch; });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1A3A5C]" /></div>;

  return (
    <div className="animate-fade-in" data-testid="social-profiles-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Profili Social</h1>
          <p className="page-subtitle">Gestisci i canali di pubblicazione e collegali a Buffer</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadBufferChannels} data-testid="refresh-buffer-btn">
            Aggiorna canali Buffer
          </Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY_FORM); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white gap-1.5" data-testid="add-profile-btn">
                <Plus className="w-4 h-4" /> Nuovo profilo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-[#1A3A5C]">{editing ? 'Modifica profilo' : 'Nuovo profilo'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#64748B]">Nome *</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Es. LinkedIn Aziendale HumanAI" data-testid="profile-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#64748B]">Piattaforma</Label>
                  <Select value={form.platform} onValueChange={v => setForm(f => ({ ...f, platform: v }))}>
                    <SelectTrigger data-testid="profile-platform-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PLATFORMS).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.platform === 'linkedin_personal' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-[#64748B]">Titolare</Label>
                    <Input value={form.owner} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} placeholder="Nome e cognome" data-testid="profile-owner-input" />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#64748B]">Note</Label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Note sul profilo..." rows={2} className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" data-testid="profile-notes-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#64748B]">Guida di stile</Label>
                  <textarea value={form.style_guide} onChange={e => setForm(f => ({ ...f, style_guide: e.target.value }))} placeholder="Tono, stile, personalita..." rows={3} className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" data-testid="profile-style-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#64748B]">Canale Buffer associato</Label>
                  <Select value={form.buffer_profile_id || 'none'} onValueChange={v => setForm(f => ({ ...f, buffer_profile_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="profile-buffer-select"><SelectValue placeholder="Seleziona canale Buffer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun canale associato</SelectItem>
                      {bufferChannels.map(bp => (
                        <SelectItem key={bp.id} value={bp.id}>
                          {bp.service} - {bp.display_name || bp.name || bp.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {bufferChannels.length === 0 && (
                    <p className="text-[10px] text-amber-600">Nessun canale Buffer trovato. Configura il token in Impostazioni &gt; Integrazioni.</p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-xs font-semibold text-[#64748B]">Priorita</Label>
                  <Input type="number" min={1} max={10} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 1 }))} className="w-20" data-testid="profile-priority-input" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                  <Button type="submit" className="bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-white" disabled={saving} data-testid="profile-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                    {editing ? 'Salva' : 'Crea'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Buffer channels summary */}
      {bufferChannels.length > 0 && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <Link2 className="w-4 h-4 text-emerald-600" />
          <span className="text-xs text-emerald-700">
            <b>{bufferChannels.length}</b> canale/i Buffer disponibili: {bufferChannels.map(c => `${c.display_name || c.name} (${c.service})`).join(', ')}
          </span>
        </div>
      )}

      {/* Profiles list */}
      <div className="space-y-3" data-testid="profiles-list">
        {profiles.map(p => {
          const platformCfg = PLATFORMS[p.platform] || PLATFORMS.linkedin_company;
          const PIcon = platformCfg.icon;
          const bufferChannel = bufferMap[p.buffer_profile_id];

          return (
            <Card key={p.profile_id} className={`border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors ${!p.active ? 'opacity-60' : ''}`} data-testid={`profile-card-${p.profile_id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${p.active ? platformCfg.color : 'bg-slate-200 text-slate-400'}`}>
                  <PIcon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <h3 className="text-sm font-semibold text-[#1A3A5C]">{p.name}</h3>
                    <Badge variant="outline" className="text-[10px]">{platformCfg.label}</Badge>
                    {!p.active && <Badge variant="outline" className="text-[10px] text-slate-400">Disattivato</Badge>}
                    {p.buffer_profile_id && (
                      <Badge className="text-[10px] bg-emerald-100 text-emerald-700" data-testid={`profile-buffer-linked-${p.profile_id}`}>
                        Buffer: {bufferChannel ? `${bufferChannel.display_name || bufferChannel.name}` : p.buffer_profile_id.substring(0, 8)}
                      </Badge>
                    )}
                    {!p.buffer_profile_id && (
                      <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Non collegato a Buffer</Badge>
                    )}
                  </div>
                  {p.owner && <p className="text-[10px] text-[#64748B]">Titolare: {p.owner}</p>}
                  {p.notes && <p className="text-[10px] text-[#94A3B8] truncate">{p.notes}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch checked={p.active} onCheckedChange={() => handleToggle(p)} data-testid={`profile-toggle-${p.profile_id}`} />
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(p)} data-testid={`profile-edit-${p.profile_id}`}>
                    <Pencil className="w-3.5 h-3.5 text-[#64748B]" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(p.profile_id)} data-testid={`profile-delete-${p.profile_id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {profiles.length === 0 && (
          <div className="text-center py-12 text-[#94A3B8]">
            <p className="text-sm">Nessun profilo configurato. Clicca "Nuovo profilo" per iniziare.</p>
            <p className="text-xs mt-1">Crea profili per LinkedIn Aziendale, LinkedIn Personale, Instagram, ecc. e collegali a Buffer.</p>
          </div>
        )}
      </div>
    </div>
  );
}
